import { ExternalLink, Building2, Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AffiliatePartner } from '@/hooks/useAffiliatePartners';
import { useAffiliateClicks } from '@/hooks/useAffiliateClicks';

interface AffiliatePartnerCardProps {
  partner: AffiliatePartner;
  compact?: boolean;
}

export const AffiliatePartnerCard = ({ partner, compact = false }: AffiliatePartnerCardProps) => {
  const { mutate: trackClick } = useAffiliateClicks();

  const handleClick = () => {
    trackClick({ partner, referrer: window.location.href });
    window.open(partner.affiliate_url, '_blank', 'noopener,noreferrer');
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3 w-3 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  if (compact) {
    return (
      <Card className="hover:shadow-lg transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-muted rounded-lg">
              {partner.logo_url ? (
                <img
                  src={partner.logo_url}
                  alt={partner.partner_name}
                  className="max-w-full max-h-full object-contain p-2"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <Building2 className={`w-8 h-8 text-muted-foreground ${partner.logo_url ? 'hidden' : ''}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-sm">{partner.partner_name}</h4>
                {partner.star_rating && renderStars(partner.star_rating)}
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{partner.description}</p>
            </div>
            <Button
              size="sm"
              onClick={handleClick}
              className="flex-shrink-0"
            >
              {partner.button_text || 'Visit'}
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-lg transition-shadow h-full flex flex-col">
      <CardHeader>
        <div className="w-full h-24 flex items-center justify-center bg-muted rounded-lg mb-4">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={partner.partner_name}
              className="max-w-full max-h-full object-contain p-4"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <Building2 className={`w-12 h-12 text-muted-foreground ${partner.logo_url ? 'hidden' : ''}`} />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <CardTitle className="text-lg">{partner.partner_name}</CardTitle>
          {partner.star_rating && renderStars(partner.star_rating)}
        </div>
        <CardDescription className="line-clamp-3">{partner.description}</CardDescription>
        {partner.personal_endorsement && (
          <div className="mt-3 p-2 bg-accent/20 rounded text-xs italic border-l-2 border-primary">
            "{partner.personal_endorsement}"
          </div>
        )}
      </CardHeader>
      <CardContent className="mt-auto">
        <Button
          onClick={handleClick}
          className="w-full"
        >
          {partner.button_text || 'Book Now'}
          <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};
